import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "../router";
import { api } from "../api";
import ContentBlockView from "../components/ContentBlockView";

// How often we ping the server with accumulated watch-time while a lesson
// with a minimum-watch-time requirement is the active, visible tab. Kept
// well under the server's MAX_HEARTBEAT_SECONDS cap (30s) with margin.
const HEARTBEAT_SECONDS = 10;

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function flattenLessons(chapters) {
  const flat = [];
  chapters.forEach((chapter, ci) => {
    chapter.lessons.forEach((lesson) => flat.push({ ...lesson, chapterTitle: chapter.title, chapterIndex: ci }));
  });
  return flat;
}

function formatWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function daysLeft(accessUntil) {
  if (!accessUntil) return null;
  const ms = new Date(accessUntil).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

// A locked chapter/lesson shows exactly ONE of these four messages, checked
// in this priority order: an unmet dependency (complete the previous
// section/chapter) always wins; only once that's satisfied do we ever
// mention a still-pending timer, and among timers it's whichever single
// delay type this item is actually configured with (hours, midnight, or
// days - a given rule only ever has one delay type at a time).
//   1. Complete previous section/chapter before you can continue to this section/chapter.
//   2. Wait until X hour(s) before you can continue to this section/chapter.
//   3. Wait until midnight before you can continue to this section/chapter.
//   4. Wait until X day(s) before you can continue to this section/chapter.
// `thisUnit` is what's actually locked ("section" for a lesson, "chapter"
// for a chapter). A lesson locked because its own rule is unmet depends on
// a previous LESSON ("section"); a lesson locked because its whole chapter
// is locked (reason prefixed "chapter_") depends on a previous CHAPTER.
function lockMessage(reason, delayValue, thisUnit) {
  const n = delayValue || 1;
  switch (reason) {
    case "dependency":
      return `Complete previous section before you can continue to this ${thisUnit}.`;
    case "chapter_dependency":
    case "chapter_locked":
      return `Complete previous chapter before you can continue to this ${thisUnit}.`;
    case "time_hours":
    case "chapter_time_hours":
      return `Wait until ${n} hour${n === 1 ? "" : "s"} before you can continue to this ${thisUnit}.`;
    case "time_next_day":
    case "chapter_time_next_day":
      return `Wait until midnight before you can continue to this ${thisUnit}.`;
    case "time_days":
    case "chapter_time_days":
      return `Wait until ${n} day${n === 1 ? "" : "s"} before you can continue to this ${thisUnit}.`;
    default:
      return `This ${thisUnit} is locked.`;
  }
}

function lessonUnlockMessage(lesson) {
  return lockMessage(lesson.lockReason, lesson.unlockDelayValue, "section");
}

function chapterUnlockMessage(chapter) {
  return lockMessage(chapter.lockReason, chapter.unlockDelayValue, "chapter");
}

export default function CoursePlayerPage({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [certificateCode, setCertificateCode] = useState(null);
  const [watchedSeconds, setWatchedSeconds] = useState(0);

  // Lesson quiz state - questions are fetched separately from the outline
  // (which only carries { id, passPercent, passed }) once a lesson with a
  // quiz becomes active, since the outline never includes question/option
  // data (keeps that endpoint light and answer-free until actually needed).
  const [quizQuestions, setQuizQuestions] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [quizError, setQuizError] = useState("");

  // Final exam state - a separate "view" from the lesson list, selected via
  // its own sidebar entry rather than being one of the flattened lessons.
  const [showFinalExam, setShowFinalExam] = useState(false);
  const [examQuestions, setExamQuestions] = useState(null);
  const [examAnswers, setExamAnswers] = useState({});
  const [examSubmitting, setExamSubmitting] = useState(false);
  const [examResult, setExamResult] = useState(null);
  const [examError, setExamError] = useState("");

  const load = () => {
    api
      .get(`/api/courses/${slug}`)
      .then((d) => {
        setData(d);
        if (!activeLessonId) {
          const flat = flattenLessons(d.chapters);
          const firstIncomplete = flat.find((l) => !l.completed) || flat[0];
          if (firstIncomplete) setActiveLessonId(firstIncomplete.id);
        }
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = useMemo(() => (data ? flattenLessons(data.chapters) : []), [data]);
  const activeLesson = flat.find((l) => l.id === activeLessonId);
  const activeIndex = flat.findIndex((l) => l.id === activeLessonId);
  const minWatchSeconds = activeLesson?.minWatchSeconds || 0;
  const watchTimeSatisfied = !minWatchSeconds || watchedSeconds >= minWatchSeconds;

  // Reset the local watch-time counter whenever the active LESSON changes
  // (not on every data reload) - initialized from what the server already
  // has recorded for this student+lesson, so returning later resumes from
  // that point instead of starting over.
  useEffect(() => {
    setWatchedSeconds(activeLesson?.watchedSeconds || 0);
  }, [activeLessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch this lesson's quiz questions (answer-free) whenever the active
  // lesson changes, but only if it actually has one configured.
  useEffect(() => {
    setQuizQuestions(null);
    setQuizAnswers({});
    setQuizResult(null);
    setQuizError("");
    if (activeLesson?.quiz) {
      api
        .get(`/api/lessons/${activeLessonId}/quiz`)
        .then((d) => setQuizQuestions(d.quiz ? d.quiz.questions : []))
        .catch((e) => setQuizError(e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLessonId, activeLesson?.quiz?.id]);

  useEffect(() => {
    if (!showFinalExam || !data?.course?.finalExam) return;
    setExamQuestions(null);
    setExamAnswers({});
    setExamResult(null);
    setExamError("");
    api
      .get(`/api/courses/${data.course.id}/final-exam`)
      .then((d) => setExamQuestions(d.quiz ? d.quiz.questions : []))
      .catch((e) => setExamError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFinalExam]);

  const submitQuiz = async () => {
    setQuizSubmitting(true);
    setQuizError("");
    try {
      const result = await api.post(`/api/lessons/${activeLessonId}/quiz/attempts`, { answers: quizAnswers });
      setQuizResult(result.attempt);
      load();
    } catch (err) {
      setQuizError(err.message);
    } finally {
      setQuizSubmitting(false);
    }
  };

  const submitExam = async () => {
    setExamSubmitting(true);
    setExamError("");
    try {
      const result = await api.post(`/api/courses/${data.course.id}/final-exam/attempts`, { answers: examAnswers });
      setExamResult(result.attempt);
      if (result.certificateIssued) setCertificateCode(result.certificateCode);
      // The server already drew the NEXT attempt's question set (at least
      // 70% different from this one, if this attempt failed) and returned
      // it right here - no second round trip needed to set up a retake.
      setExamQuestions(result.quiz.questions);
      setExamAnswers({});
      load();
    } catch (err) {
      setExamError(err.message);
    } finally {
      setExamSubmitting(false);
    }
  };

  // Accumulates real engaged time (tab open on this lesson AND visible) and
  // periodically reports it to the server, which is the source of truth for
  // whether "Mark lesson complete" may enable. Stops entirely once the
  // requirement is met (the effect re-runs when `watchTimeSatisfied` flips
  // and simply doesn't start a new interval), so it doesn't run forever.
  useEffect(() => {
    if (!activeLesson || activeLesson.locked || activeLesson.completed) return undefined;
    if (watchTimeSatisfied) return undefined;

    let unsent = 0;
    const lessonId = activeLesson.id;

    const flush = (seconds) => {
      if (seconds <= 0) return;
      api
        .post(`/api/lessons/${lessonId}/watch-heartbeat`, { seconds })
        .then((result) => setWatchedSeconds(result.watchedSeconds))
        .catch(() => {
          /* transient network hiccup - the next tick will just try again */
        });
    };

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      unsent += 1;
      setWatchedSeconds((s) => s + 1); // optimistic local tick for a smooth countdown
      if (unsent >= HEARTBEAT_SECONDS) {
        const toSend = unsent;
        unsent = 0;
        flush(toSend);
      }
    };
    const intervalId = setInterval(tick, 1000);

    const flushRemainder = () => {
      if (unsent > 0) {
        const toSend = unsent;
        unsent = 0;
        flush(toSend);
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flushRemainder();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", flushRemainder);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", flushRemainder);
      flushRemainder();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLesson?.id, activeLesson?.locked, activeLesson?.completed, watchTimeSatisfied]);

  const markComplete = async () => {
    setCompleting(true);
    try {
      const result = await api.post(`/api/lessons/${activeLessonId}/complete`, {});
      if (result.certificateIssued) setCertificateCode(result.certificateCode);
      // move to next lesson automatically
      const next = flat[activeIndex + 1];
      load();
      if (next) setActiveLessonId(next.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCompleting(false);
    }
  };

  if (error && !data) return <div className="page"><div className="alert alert-error">{error}</div></div>;
  if (!data) return <div className="page"><p className="muted">Loading…</p></div>;

  if (data.course.accessExpired) {
    return (
      <div className="page page-narrow">
        <div className="alert alert-error">
          Your access to this course expired on {formatWhen(data.course.accessUntil)}.{" "}
          <Link to="/">Go to course catalog</Link>
        </div>
      </div>
    );
  }

  if (!data.course.enrolled) {
    return (
      <div className="page page-narrow">
        <div className="alert alert-error">
          You need to enroll in this course first. <Link to="/">Go to course catalog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="player-layout">
      <aside className="player-sidebar">
        <Link to="/" className="player-back">&larr; {data.course.title}</Link>
        {data.course.enrolledAt && (
          <div className="muted" style={{ fontSize: 12 }}>
            Started {formatDate(data.course.enrolledAt)}
          </div>
        )}
        {data.course.accessUntil && (
          <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
            {daysLeft(data.course.accessUntil)} day{daysLeft(data.course.accessUntil) === 1 ? "" : "s"} left &middot;
            expires {formatWhen(data.course.accessUntil)}
          </div>
        )}
        {data.chapters.map((chapter, ci) => (
          <div key={chapter.id} className="player-chapter">
            <div className="player-chapter-title" title={chapter.locked ? chapterUnlockMessage(chapter) : undefined}>
              Chapter {ci + 1}: {chapter.title}
              {chapter.locked && <span className="lesson-lock-icon"> 🔒</span>}
            </div>
            {chapter.lessons.map((lesson) => (
              <button
                key={lesson.id}
                className={`player-lesson ${!showFinalExam && lesson.id === activeLessonId ? "active" : ""} ${lesson.locked ? "locked" : ""}`}
                onClick={() => {
                  setShowFinalExam(false);
                  setActiveLessonId(lesson.id);
                }}
                title={lesson.locked ? lessonUnlockMessage(lesson) : undefined}
              >
                <span className={`lesson-dot ${lesson.completed ? "done" : ""} ${lesson.locked ? "locked" : ""}`}>
                  {lesson.completed ? "✓" : lesson.locked ? <span className="lesson-lock-icon">🔒</span> : ""}
                </span>
                {lesson.title}
              </button>
            ))}
          </div>
        ))}
        {data.course.finalExam && (
          <div className="player-chapter">
            <div className="player-chapter-title">Final Exam</div>
            <button
              className={`player-lesson ${showFinalExam ? "active" : ""}`}
              onClick={() => setShowFinalExam(true)}
            >
              <span className={`lesson-dot ${data.course.finalExam.passed ? "done" : ""}`}>
                {data.course.finalExam.passed ? "✓" : "🎓"}
              </span>
              Final Exam
            </button>
          </div>
        )}
      </aside>

      <main className="player-main">
        {certificateCode && (
          <div className="alert alert-success certificate-banner">
            🎉 Congratulations - you've completed the course!{" "}
            <a href={`/api/certificates/${certificateCode}/download`} className="btn btn-primary btn-sm">
              Download your certificate
            </a>
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        {showFinalExam ? (
          <>
            <div className="player-lesson-header">
              <h1>Final Exam</h1>
            </div>
            {examError && <div className="alert alert-error">{examError}</div>}
            {examResult && (
              <p className={examResult.passed ? "quiz-result-pass" : "quiz-result-fail"}>
                Last attempt: {examResult.scorePercent}% ({examResult.correctCount}/{examResult.totalQuestions}) -{" "}
                {examResult.passed
                  ? "Passed! Your certificate is ready."
                  : `You need ${examResult.passPercent}% to pass - try again below.`}
              </p>
            )}
            {examQuestions === null ? (
              <p className="muted">Loading…</p>
            ) : examQuestions.length === 0 ? (
              <p className="muted">This exam doesn't have any questions yet - check back later.</p>
            ) : (
              <>
                <p className="muted">
                  You need {data.course.finalExam.passPercent}% or higher to earn your certificate.
                  {data.course.finalExam.passed && " You've already passed - you can retake it any time."}
                  {examResult && !examResult.passed && " Here's a new set of questions - most are different from your last attempt."}
                </p>
                {examQuestions.map((q, qi) => (
                  <div className="quiz-question" key={q.id}>
                    <p className="quiz-question-prompt">
                      {qi + 1}. {q.prompt}
                    </p>
                    {q.options.map((o) => (
                      <label className="quiz-option" key={o.id}>
                        <input
                          type="radio"
                          name={`exam-q-${q.id}`}
                          checked={examAnswers[q.id] === o.id}
                          onChange={() => setExamAnswers({ ...examAnswers, [q.id]: o.id })}
                        />
                        {o.text}
                      </label>
                    ))}
                  </div>
                ))}
                <button
                  className="btn btn-primary"
                  onClick={submitExam}
                  disabled={examSubmitting || Object.keys(examAnswers).length < examQuestions.length}
                >
                  {examSubmitting ? "Submitting…" : data.course.finalExam.passed ? "Retake exam" : "Submit exam"}
                </button>
              </>
            )}
          </>
        ) : activeLesson ? (
          <>
            <div className="player-lesson-header">
              <span className="muted">{activeLesson.chapterTitle}</span>
              <h1>{activeLesson.title}</h1>
            </div>
            {activeLesson.locked ? (
              <div className="locked-lesson-panel">
                <div className="lock-icon">🔒</div>
                <p>{lessonUnlockMessage(activeLesson)}</p>
              </div>
            ) : (
              <>
                {(activeLesson.contentBlocks || []).map((block) => (
                  <ContentBlockView block={block} key={block.id} />
                ))}
                {activeLesson.quiz && (
                  <div className="lesson-quiz-panel">
                    <h3>
                      Lesson quiz{" "}
                      {activeLesson.quiz.passed && <span className="badge badge-success">Passed</span>}
                    </h3>
                    <p className="muted">You need {activeLesson.quiz.passPercent}% or higher to pass.</p>
                    {quizError && <div className="alert alert-error">{quizError}</div>}
                    {quizQuestions === null ? (
                      <p className="muted">Loading quiz…</p>
                    ) : quizQuestions.length === 0 ? (
                      <p className="muted">This quiz doesn't have any questions yet - check back later.</p>
                    ) : (
                      <>
                        {quizQuestions.map((q, qi) => (
                          <div className="quiz-question" key={q.id}>
                            <p className="quiz-question-prompt">
                              {qi + 1}. {q.prompt}
                            </p>
                            {q.options.map((o) => (
                              <label className="quiz-option" key={o.id}>
                                <input
                                  type="radio"
                                  name={`quiz-q-${q.id}`}
                                  checked={quizAnswers[q.id] === o.id}
                                  onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: o.id })}
                                />
                                {o.text}
                              </label>
                            ))}
                          </div>
                        ))}
                        <button
                          className="btn btn-secondary"
                          onClick={submitQuiz}
                          disabled={quizSubmitting || Object.keys(quizAnswers).length < quizQuestions.length}
                        >
                          {quizSubmitting ? "Submitting…" : activeLesson.quiz.passed ? "Retake quiz" : "Submit quiz"}
                        </button>
                        {quizResult && (
                          <p className={quizResult.passed ? "quiz-result-pass" : "quiz-result-fail"}>
                            Score: {quizResult.scorePercent}% ({quizResult.correctCount}/{quizResult.totalQuestions}) -{" "}
                            {quizResult.passed
                              ? "Passed!"
                              : `You need ${quizResult.passPercent}% to pass - try again.`}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div className="player-actions">
                  {activeLesson.completed ? (
                    <span className="badge badge-success">Lesson complete</span>
                  ) : (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={markComplete}
                        disabled={completing || !watchTimeSatisfied || (activeLesson.quiz && !activeLesson.quiz.passed)}
                      >
                        {completing ? "Saving…" : "Mark lesson complete"}
                      </button>
                      {!watchTimeSatisfied && (
                        <p className="muted watch-time-notice">
                          Keep this lesson open - {formatDuration(minWatchSeconds - watchedSeconds)} remaining
                          before you can mark it complete.
                        </p>
                      )}
                      {watchTimeSatisfied && activeLesson.quiz && !activeLesson.quiz.passed && (
                        <p className="muted watch-time-notice">
                          Pass the lesson quiz above before you can mark this lesson complete.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
            <div className="player-nav">
              <button
                className="btn btn-secondary"
                onClick={() => setActiveLessonId(flat[activeIndex - 1].id)}
                disabled={activeIndex <= 0}
              >
                &larr; Previous
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActiveLessonId(flat[activeIndex + 1].id)}
                disabled={activeIndex === -1 || activeIndex >= flat.length - 1}
              >
                Next &rarr;
              </button>
            </div>
          </>
        ) : (
          <p className="muted">This course has no lessons yet.</p>
        )}
      </main>
    </div>
  );
}
